"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from "react";
import { MEMES_MANIFOLD_PROXY_ABI } from "@/abis/abis";
import { useCookieConsent } from "@/components/cookies/CookieConsentContext";
import { MANIFOLD_LAZY_CLAIM_CONTRACT } from "@/constants/constants";
import useCapacitor from "@/hooks/useCapacitor";
import { ManifoldClaimStatus, ManifoldPhase, useManifoldClaim } from "@/hooks/useManifoldClaim";
import type { Abi } from "viem";
export interface CountdownData {
  readonly title: string;
  readonly targetDate: number;
  readonly showAllowlistInfo: boolean;
  readonly showMintBtn: boolean;
  readonly isActive: boolean;
}
export type MintCountdownState = {
  type: "loading";
} | {
  type: "error";
} | {
  type: "sold_out";
} | {
  type: "finalized";
} | {
  type: "countdown";
  countdown: CountdownData;
};
interface UseMintCountdownStateOptions {
  hideMintBtn?: boolean | undefined;
  contract: string;
  chainId: number;
  abi?: Abi | undefined;
}
export function useMintCountdownState(nftId, opts) {
  const $ = _c(16);
  const [errorFromCallback, setErrorFromCallback] = useState(false);
  const {
    contract,
    chainId,
    abi: t0,
    hideMintBtn
  } = opts;
  const abi = t0 === undefined ? MEMES_MANIFOLD_PROXY_ABI : t0;
  const [prevNftId, setPrevNftId] = useState(nftId);
  if (nftId !== prevNftId) {
    setPrevNftId(nftId);
    setErrorFromCallback(false);
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setErrorFromCallback(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleManifoldClaimError = t1;
  let t2;
  if ($[1] !== abi || $[2] !== chainId || $[3] !== contract || $[4] !== nftId) {
    t2 = {
      chainId,
      contract,
      proxy: MANIFOLD_LAZY_CLAIM_CONTRACT,
      abi,
      identifier: nftId,
      onError: handleManifoldClaimError
    };
    $[1] = abi;
    $[2] = chainId;
    $[3] = contract;
    $[4] = nftId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    claim: manifoldClaim
  } = useManifoldClaim(t2);
  const isError = errorFromCallback && (!manifoldClaim || manifoldClaim.isError);
  const {
    isIos
  } = useCapacitor();
  const {
    country
  } = useCookieConsent();
  const showMintBtn = !hideMintBtn && !(isIos && country !== "US");
  let t3;
  bb0: {
    if (isError) {
      let t4;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = {
          type: "error"
        };
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      t3 = t4;
      break bb0;
    }
    if (!manifoldClaim) {
      let t4;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = {
          type: "loading"
        };
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      t3 = t4;
      break bb0;
    }
    if (manifoldClaim.isSoldOut) {
      let t4;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = {
          type: "sold_out"
        };
        $[8] = t4;
      } else {
        t4 = $[8];
      }
      t3 = t4;
      break bb0;
    }
    if (manifoldClaim.isFinalized) {
      let t4;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = {
          type: "finalized"
        };
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      t3 = t4;
      break bb0;
    }
    const phaseName = manifoldClaim.phase === ManifoldPhase.ALLOWLIST && manifoldClaim.memePhase ? manifoldClaim.memePhase.name : manifoldClaim.phase;
    const isUpcoming = manifoldClaim.status === ManifoldClaimStatus.UPCOMING;
    const t4 = `${phaseName} ${isUpcoming ? "Starts In" : "Ends In"}`;
    const t5 = isUpcoming ? manifoldClaim.startDate : manifoldClaim.endDate;
    const t6 = manifoldClaim.phase === ManifoldPhase.ALLOWLIST;
    const t7 = !isUpcoming;
    let t8;
    if ($[10] !== showMintBtn || $[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
      t8 = {
        type: "countdown",
        countdown: {
          title: t4,
          targetDate: t5,
          showAllowlistInfo: t6,
          showMintBtn,
          isActive: t7
        }
      };
      $[10] = showMintBtn;
      $[11] = t4;
      $[12] = t5;
      $[13] = t6;
      $[14] = t7;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    t3 = t8;
  }
  return t3;
}